import { Component, ViewEncapsulation, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Observable } from 'rxjs';

import { Price } from '@app/entities/price';
import { PassengerArrangement, PassengerType } from '@shared/base-search-bar/entities/passenger-arrangement';
import { TagMemberType } from '@design-system/components/molecules/tag-member/entities/tag-member.types';
import {
  getPaymentDiscountAmount, getPaymentPercentageCalculated, getStrikethroughDiscount
} from '@shared/payment-detail/mappers/discount-calculated.mapper';
import {
  PriceDetailCardDiscount, PriceDetailCardPrice
} from '@design-system/components/organisms/price-detail-card/entities/price-detail-card.types';
import { PriceDetailService } from '@shared/payment-detail/services/payment-detail.service';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';

@Component({
  selector: 'app-package-payment-detail',
  templateUrl: './package-payment-detail.component.html',
  styleUrls: [],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PackagePaymentDetailComponent {

  @Input() price: Price;
  @Input() passengerArrangement: PassengerArrangement[];
  @Input() conversionRate: number;

  @Input() isMobile = false;
  @Input() isModal = false;
  @Input() showSwipeUp = false;
  @Input() productReference: number;

  @Output() toggleShowSwipeUp: EventEmitter<Event> = new EventEmitter<Event>();

  private translateRoot: string = 'PACKAGES_MODULES.PACKAGE_BOOKING.PAYMENT_DETAIL.PRICE_TO_PACKAGE';

  public readonly tagMemberType = TagMemberType;

  constructor(
    private readonly translation: TranslocoService,
    private readonly service: PriceDetailService,
  ) { }

  get discount$(): Observable<PriceDetailCardDiscount> {
    return this.service.getCardDiscount$(
      SearchBy.PACKAGES, this.discountAmount, this.discountPercentageValueProp, this.price?.CurrencyCode
    );
  }

  get rows(): PriceDetailCardPrice[][] {
    return [
      this.service.getCardGenericPaymentDetailSection(
        this.price?.Amount, this.price?.Taxes, this.price?.Fees, this.price?.CurrencyCode
      )
    ];
  }

  get adultsQuantity(): number {
    return this.mapPassengerArrengement
      .filter(passenger => passenger.id === PassengerType.ADULTS).reduce((a, b) => a + (b.quantity || 0), 0);
  }

  get childrenQuantity(): number {
    return this.mapPassengerArrengement
      .filter(passenger => passenger.id === PassengerType.CHILDREN).reduce((a, b) => a + (b.quantity || 0), 0);
  }

  get infantsQuantity(): number {
    return this.mapPassengerArrengement
      .filter(passenger => passenger.id === PassengerType.INFANTS).reduce((a, b) => a + (b.quantity || 0), 0);
  }

  get mapPassengerArrengement(): PassengerArrangement[] {
    return this.passengerArrangement ? this.passengerArrangement.filter(data => data.quantity > 0) : [];
  }

  get hasAdults(): boolean {
    return !!this.adultsQuantity;
  }

  get hasChildrens(): boolean {
    return !!this.childrenQuantity;
  }

  get hasInfants(): boolean {
    return !!this.infantsQuantity;
  }

  get adultsTranslate(): string {
    return this.hasAdults && this.adultsQuantity > 1 ? 'ADULTS' : 'ADULT';
  }

  get childrenTranslate(): string {
    return this.hasChildrens && this.childrenQuantity > 1 ? 'MANY_CHILDREN' : 'CHILDREN';
  }

  get infantTranslate(): string {
    return this.hasInfants && this.infantsQuantity > 1 ? 'INFANTS' : 'INFANT';
  }

  get descriptionsTranslate(): string {
    if (this.hasAdults && this.hasChildrens && this.hasInfants) {
      return ` ${this.adultsQuantity} ${this.translation.translate(`${this.translateRoot}.${this.adultsTranslate}`)}, ` +
        `${this.childrenQuantity} ${this.translation.translate(`${this.translateRoot}.${this.childrenTranslate}`)}, ` +
        `${this.infantsQuantity} ${this.translation.translate(`${this.translateRoot}.${this.infantTranslate}`)}`;
    } else if (this.hasAdults && this.hasChildrens && !this.hasInfants) {
      return ` ${this.adultsQuantity} ${this.translation.translate(`${this.translateRoot}.${this.adultsTranslate}`)}, ` +
        `${this.childrenQuantity} ${this.translation.translate(`${this.translateRoot}.${this.childrenTranslate}`)}`;
    } else if (this.hasAdults && !this.hasChildrens && this.hasInfants) {
      return ` ${this.adultsQuantity} ${this.translation.translate(`${this.translateRoot}.${this.adultsTranslate}`)}, ` +
        `${this.infantsQuantity} ${this.translation.translate(`${this.translateRoot}.${this.infantTranslate}`)}`;
    } else if (this.hasAdults && !this.hasChildrens && !this.hasInfants) {
      return ` ${this.adultsQuantity} ${this.translation.translate(`${this.translateRoot}.${this.adultsTranslate}`)}`;
    } else return '';
  }

  get discountAmount(): number {
    return getPaymentDiscountAmount(
      this.price?.Strikethrough, this.price?.Total, this.price?.DiscountAmount, this.strikethroughDiscount
    );
  }

  get discountPercentageValueProp(): number {
    return getPaymentPercentageCalculated(
      true, this.price?.Strikethrough, this.price?.Total, this.price?.DiscountAmount,
      this.strikethroughDiscount, this.price?.Amount, this.price?.Taxes,
    );
  }

  private get strikethroughDiscount(): number {
    return getStrikethroughDiscount(this.price?.TotalDiscountInStrikethrough, this.price?.TotalDiscount);
  }

  public existAmount(amount: number): boolean {
    return amount !== 0;
  }
}
